import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import PapaParse from 'papaparse'

import { Col, View } from '@amzn/stencil-react-components/layout'
import { MessageBanner, MessageBannerType } from '@amzn/stencil-react-components/message-banner'

import { ItemContainerMetadataDTO } from 'src/models/dto/item-pools/ItemPoolMetadataDTO'
import { TemplateValidationErrorsThrowable } from 'src/models/dto/TemplateValidationError'
import { CreateStep } from 'src/pages/import-content/CreateStep'
import { ImportConfirmationModal } from 'src/pages/import-content/ImportConfirmationModal'
import { ImportContentHeader } from 'src/pages/import-content/ImportContentHeader'
import {
    countTemplateItems,
    groupRowsByContainer,
    ImportStep,
    mustSkipContainerCreation,
    ParsedSpreadsheet,
    planContainers,
    validateSpreadsheet,
} from 'src/pages/import-content/ImportContentModel'
import { ReviewStep } from 'src/pages/import-content/ReviewStep'
import { UploadStep } from 'src/pages/import-content/UploadStep'
import { useImportSpreadsheetUpload } from 'src/pages/import-content/useImportSpreadsheetUpload'
import { useRowValidation } from 'src/pages/import-content/useRowValidation'
import { ModuleService } from 'src/services/backend/ModuleService'
import {
    CONTAINER_COLUMN,
    downloadSampleSpreadsheet,
    getSpreadsheetColumns,
} from 'src/services/import-content/TemplateSpreadsheetService'
import { ItemPoolService } from 'src/services/item-pools/ItemPoolService'

const MODULE_VERSION_ID_PARAM_KEY = 'moduleVersionId'

export const ImportContentPage = () => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const templateModuleVersionId = (searchParams.get(MODULE_VERSION_ID_PARAM_KEY) ?? '').trim()

    const [step, setStep] = useState(ImportStep.Upload)

    const [moduleName, setModuleName] = useState<string | undefined>(undefined)
    const [savedAt, setSavedAt] = useState<Date | undefined>(undefined)
    const [placeholders, setPlaceholders] = useState<string[]>([])
    const [itemsPerRow, setItemsPerRow] = useState(0)
    const [isLoadingModule, setIsLoadingModule] = useState(false)
    const [loadError, setLoadError] = useState<string | undefined>(undefined)

    const [file, setFile] = useState<File | undefined>(undefined)
    const [spreadsheet, setSpreadsheet] = useState<ParsedSpreadsheet | undefined>(undefined)
    const [reviewPage, setReviewPage] = useState(0)
    const [skipContainerCreation, setSkipContainerCreation] = useState(false)
    const [isConfirmingImport, setIsConfirmingImport] = useState(false)

    const [isPreviewing, setIsPreviewing] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
    const [containers, setContainers] = useState<ItemContainerMetadataDTO[] | undefined>(undefined)
    const [itemPoolVersionId, setItemPoolVersionId] = useState<string | undefined>(undefined)
    const [isItemPoolSaved, setIsItemPoolSaved] = useState(false)
    const isImported = containers !== undefined || itemPoolVersionId !== undefined

    const uploadSpreadsheet = useImportSpreadsheetUpload()
    const {
        rowValidation,
        plansByContainer,
        isValidatingRows,
        validatedRowCount,
        resetRowValidation,
    } = useRowValidation({
        step,
        spreadsheet,
        file,
        skipContainerCreation,
        templateModuleVersionId,
        uploadSpreadsheet,
    })

    const plannedContainers = useMemo(
        () =>
            spreadsheet && !skipContainerCreation
                ? planContainers(spreadsheet.rows, plansByContainer)
                : [],
        [spreadsheet, skipContainerCreation, plansByContainer]
    )

    const validation = useMemo(
        () => (spreadsheet ? validateSpreadsheet(spreadsheet, placeholders) : undefined),
        [spreadsheet, placeholders]
    )

    const loadTemplate = useCallback(async (versionId: string) => {
        setLoadError(undefined)
        setModuleName(undefined)
        setSavedAt(undefined)
        setPlaceholders([])
        setIsLoadingModule(true)

        try {
            const moduleVersion = await ModuleService.loadModuleVersionDTO(versionId)
            setModuleName(moduleVersion.content.name)
            setSavedAt(moduleVersion.updatedAt)
            setPlaceholders(getSpreadsheetColumns(moduleVersion.content))
            setItemsPerRow(countTemplateItems(moduleVersion.content))
        } catch (e) {
            console.error('Failed to load template module', e)
            setLoadError(e instanceof Error ? e.message : 'Failed to load the template module.')
        } finally {
            setIsLoadingModule(false)
        }
    }, [])

    useEffect(() => {
        if (templateModuleVersionId) {
            void loadTemplate(templateModuleVersionId)
        } else {
            setLoadError('No template module was provided. Start the import from the module.')
        }
    }, [templateModuleVersionId, loadTemplate])

    const onFileAttached = useCallback(
        (attached?: File) => {
            setFile(attached)
            setSpreadsheet(undefined)
            setReviewPage(0)
            resetRowValidation()
            setErrorMessage(undefined)
            setSkipContainerCreation(false)
            if (!attached) {
                return
            }
            PapaParse.parse<Record<string, string>>(attached, {
                header: true,
                skipEmptyLines: true,
                complete: (results) => {
                    const headers = results.meta.fields ?? []
                    const rows = results.data
                    const hasContainerColumn = headers.includes(CONTAINER_COLUMN)
                    const containerCount = hasContainerColumn
                        ? groupRowsByContainer(rows).length
                        : 1
                    const parsed = {
                        headers,
                        rowCount: rows.length,
                        containerCount,
                        rows,
                        hasContainerColumn,
                    }
                    setSpreadsheet(parsed)
                    setSkipContainerCreation(mustSkipContainerCreation(parsed))
                },
            })
        },
        [resetRowValidation]
    )

    const onDownloadSample = useCallback(() => {
        downloadSampleSpreadsheet(placeholders, moduleName)
    }, [placeholders, moduleName])

    const isSpreadsheetUsable =
        !!moduleName && !!spreadsheet && !!validation && validation.missing.length === 0

    const hasFlaggedRows = Object.keys(rowValidation?.byRow ?? {}).length > 0
    const hasGeneralErrors = (rowValidation?.general ?? []).length > 0
    // The latest validation run's general errors show alongside any preview or import error, so a
    // new run (e.g. after skipping containers) replaces them without hiding the other errors.
    const bannerMessage = [errorMessage, ...(rowValidation?.general ?? [])]
        .filter(Boolean)
        .join(' ')

    const canSubmit =
        isSpreadsheetUsable &&
        !!file &&
        !isSubmitting &&
        !isValidatingRows &&
        !hasFlaggedRows &&
        !hasGeneralErrors
    const canPreview = isSpreadsheetUsable && !isPreviewing && !isSubmitting

    const advancedFor = useRef<ParsedSpreadsheet | undefined>(undefined)
    useEffect(() => {
        if (isSpreadsheetUsable && advancedFor.current !== spreadsheet) {
            advancedFor.current = spreadsheet
            setStep(ImportStep.Review)
        }
    }, [isSpreadsheetUsable, spreadsheet])

    const onPreviewRow = useCallback(
        async (row: Record<string, string>) => {
            setErrorMessage(undefined)
            setIsPreviewing(true)
            try {
                const moduleDtoJson = await ItemPoolService.buildImportedModulePreview(
                    templateModuleVersionId,
                    [row],
                    !skipContainerCreation
                )
                const versionId = (JSON.parse(moduleDtoJson) as { version: string }).version
                const previewUrl = await ItemPoolService.previewModuleTemplate(
                    versionId,
                    moduleDtoJson
                )
                window.open(previewUrl, '_blank', 'noopener,noreferrer')
            } catch (e) {
                console.error('Preview failed', e)
                setErrorMessage(e instanceof Error ? e.message : 'Failed to generate the preview.')
            } finally {
                setIsPreviewing(false)
            }
        },
        [templateModuleVersionId, skipContainerCreation]
    )

    const onSubmit = useCallback(async () => {
        /* istanbul ignore if: Create is aria-disabled until a usable spreadsheet is attached */
        if (!file) {
            return
        }
        setErrorMessage(undefined)
        setContainers(undefined)
        setItemPoolVersionId(undefined)
        setIsItemPoolSaved(false)
        resetRowValidation()
        setIsSubmitting(true)
        setStep(ImportStep.Create)
        try {
            const spreadsheetS3Path = await uploadSpreadsheet(file)

            const { versionId } = await ItemPoolService.importedItemPool({
                templateModuleVersionId,
                spreadsheetS3Path,
                createItemContainers: !skipContainerCreation,
            })

            if (skipContainerCreation) {
                // The pool is saved asynchronously; the create step polls it until it is saved.
                setItemPoolVersionId(versionId)
            } else {
                const generated = await ItemPoolService.getItemPoolItems(versionId)
                setContainers(generated ?? [])
            }
        } catch (e) {
            console.error('Import content failed', e)
            if (e instanceof TemplateValidationErrorsThrowable) {
                setStep(ImportStep.Review)
                setErrorMessage(
                    [
                        'The spreadsheet did not pass validation. Nothing was imported.',
                        ...e.errors.map(({ message }) => message),
                    ].join(' ')
                )
            } else {
                setErrorMessage(
                    e instanceof Error ? e.message : 'Failed to import content. Please try again.'
                )
            }
        } finally {
            setIsSubmitting(false)
        }
    }, [
        file,
        templateModuleVersionId,
        skipContainerCreation,
        uploadSpreadsheet,
        resetRowValidation,
    ])

    // Importing Item Containers creates or updates modules, so the author confirms which first.
    const onCreate = useCallback(() => {
        if (skipContainerCreation) {
            void onSubmit()
        } else {
            setIsConfirmingImport(true)
        }
    }, [skipContainerCreation, onSubmit])

    const onConfirmImport = useCallback(() => {
        setIsConfirmingImport(false)
        void onSubmit()
    }, [onSubmit])

    const onStepSelected = useCallback(
        (selected: ImportStep) => {
            if (isSubmitting || isImported) {
                return
            }
            if (selected < step || (selected === ImportStep.Review && isSpreadsheetUsable)) {
                setStep(selected)
            }
        },
        [step, isSubmitting, isImported, isSpreadsheetUsable]
    )

    return (
        <View padding='S400'>
            <Col gridGap='S400' width='100%'>
                <ImportContentHeader
                    moduleName={moduleName}
                    moduleVersionId={templateModuleVersionId}
                    savedAt={savedAt}
                    currentStep={step}
                    isReviewAvailable={isSpreadsheetUsable && !isImported}
                    onStepSelected={onStepSelected}
                />

                {bannerMessage && (
                    <MessageBanner type={MessageBannerType.Error} dataTestId='import-content-error'>
                        {bannerMessage}
                    </MessageBanner>
                )}

                {step === ImportStep.Upload && (
                    <UploadStep
                        isLoadingModule={isLoadingModule}
                        loadError={loadError}
                        moduleName={moduleName}
                        file={file}
                        placeholders={placeholders}
                        spreadsheet={spreadsheet}
                        validation={validation}
                        onFileAttached={onFileAttached}
                        onDownloadSample={onDownloadSample}
                    />
                )}

                {step === ImportStep.Review && spreadsheet && (
                    <ReviewStep
                        spreadsheet={spreadsheet}
                        page={reviewPage}
                        onPageChange={setReviewPage}
                        canPreview={canPreview}
                        onPreviewRow={onPreviewRow}
                        isPreviewing={isPreviewing}
                        canSubmit={canSubmit}
                        onSubmit={onCreate}
                        onBack={() => setStep(ImportStep.Upload)}
                        rowValidation={rowValidation}
                        isValidatingRows={isValidatingRows}
                        validatedRowCount={validatedRowCount}
                        itemsPerRow={itemsPerRow}
                        skipContainerCreation={skipContainerCreation}
                        onSkipContainerCreationChange={setSkipContainerCreation}
                    />
                )}

                {step === ImportStep.Create && (
                    <CreateStep
                        fileName={file?.name}
                        rowCount={spreadsheet?.rowCount ?? 0}
                        plannedContainers={plannedContainers}
                        isSubmitting={isSubmitting}
                        containers={containers}
                        itemPoolVersionId={itemPoolVersionId}
                        isItemPoolSaved={isItemPoolSaved}
                        onItemPoolSaved={() => setIsItemPoolSaved(true)}
                        onItemPoolError={(message) =>
                            setErrorMessage(`The item pool could not be saved: ${message}`)
                        }
                        hasError={!!errorMessage && !isSubmitting}
                        onBack={() => setStep(ImportStep.Review)}
                        onDone={() =>
                            navigate(
                                `/module-builder?${MODULE_VERSION_ID_PARAM_KEY}=${templateModuleVersionId}`
                            )
                        }
                    />
                )}

                <ImportConfirmationModal
                    isOpen={isConfirmingImport}
                    containers={plannedContainers}
                    onCancel={() => setIsConfirmingImport(false)}
                    onConfirm={onConfirmImport}
                />
            </Col>
        </View>
    )
}
